import React, {useEffect, useMemo} from "react";
import {SubmitHandler, useForm} from "react-hook-form";
import {zodResolver} from "@hookform/resolvers/zod";
import {useAuthStore} from "@store/AuthStore";
import {useShallow} from "zustand/react/shallow";
import UserService from "@lib/User/UserService";
import {FORM_FIELD_TYPE, FormFieldPayload} from "@lib/Form/FormModels";
import {usePrivateBootstrap} from "@lib/Bootstrap/usePrivateBootstrap";
import {getValidAccessToken} from "@msal/tokenService";
import {buildRoute} from "@router/RouterUtils";
import {AppRoute} from "@router/RoutingModels";
import {useNavigate} from "react-router-dom";

import {TextField} from "@components/Form/TextField";
import {PrivacyField} from "@components/Form/PrivacyField";
import {removeParenthesesContent} from "@commons/Utils";
import {ChevronRightIcon} from "@components/Icons/ChevronRightIcon";
import {usePublicTranslations} from "@i18n/i18nUtils";
import {useModalStore} from "@store/ModalStore";
import {useSsoFinalizationForm} from "@lib/Finalization/FinalizationFormHooks";
import {OnboardFormSkeleton} from "@pages/Onboard/Components/OnboardForm/OnboardFormSkeleton";
import {isApiException} from "@/exceptions/ApiException";
import {
    buildOnboardSchema,
    errorModalMessage,
    OnboardFormValues,
    TOKEN_FIELDS
} from "@pages/Onboard/Components/OnboardForm/OnboardFormUtils";

export function OnboardForm() {

    const {idTokenClaims, derivedCountry} = useAuthStore(
        useShallow((s) => ({
            idTokenClaims: s.idTokenClaims,
            derivedCountry: s.derivedCountry,
        }))
    );

    const {run: runBootstrap} = usePrivateBootstrap();
    const navigate = useNavigate();
    const {t, i18n} = usePublicTranslations();
    const {data: formConfig, isError: isFormError, isLoading: isFormLoading} = useSsoFinalizationForm();

    const {openModal} = useModalStore(useShallow(s => ({
        openModal: s.open
    })));

    /**
     * Campi CMS da mostrare all'utente. esclude i campi marcati come `hidden` nella config CMS
     * L'ordinamento per `cmsConfig.order` delega interamente al CMS il controllo del layout
     */
    const visibleFields = useMemo(
        () => (formConfig?.fields ?? [])
            .filter(f => !f.cmsConfig.hidden)
            .sort((a, b) => a.cmsConfig.order - b.cmsConfig.order),
        [formConfig?.fields]
    );

    const onboardSchema = useMemo(
        () => buildOnboardSchema(visibleFields, t),
        [visibleFields, t, i18n.language]
    );

    /**
     * Valori iniziali del form: ogni campo visibile viene pre-compilato con il dato
     * ricavato dal token MSAL tramite TOKEN_FIELDS, se disponibile.
     * I campi senza corrispondenza nel token (o con valore undefined) partono vuoti.
     */
    const defaultValues = useMemo<OnboardFormValues>(() => {
        const values: OnboardFormValues = {};

        for (const field of visibleFields) {
            const fromClaims = TOKEN_FIELDS[field.hurlId]?.fromClaims;
            const claimValue = idTokenClaims ? fromClaims?.(idTokenClaims, derivedCountry) : undefined;
            values[field.hurlId] = claimValue ?? "";
        }

        return values;
    }, [visibleFields, idTokenClaims, derivedCountry]);

    const {
        control,
        handleSubmit,
        trigger,
        reset,
        formState: {errors, isSubmitting, isValid, isValidating},
    } = useForm<OnboardFormValues>({
        resolver: zodResolver(onboardSchema) as any,
        defaultValues,
        mode: "onChange",
        reValidateMode: "onChange",
    });


    useEffect(() => {
        reset(defaultValues, {keepDirtyValues: true});

        const prefilled = Object.keys(defaultValues).filter(id => defaultValues[id] !== "");

        if (prefilled.length > 0) {
            void trigger(prefilled);
        }
    }, [onboardSchema, defaultValues]);

    const inputsDisabled = isSubmitting;

    const formDisabled = isSubmitting || isValidating || !isValid;

    const submitFn: SubmitHandler<OnboardFormValues> = async (formData) => {

        if (
            !idTokenClaims ||
            !idTokenClaims.sub ||
            !derivedCountry ||
            !idTokenClaims.extension_consumerOrProfessional ||
            !idTokenClaims.upn
        ) {
            throw new Error("Required parameters missing");
        }

        const pivaValue = formData["piva"];

        if (pivaValue !== undefined) {
            try {

                const validationResponse = await UserService.validateVat(pivaValue.trim());

                if (validationResponse.result !== "OK") {
                    throw new Error(validationResponse.result);
                }

            } catch (e) {
                console.log("VAT Validation failed", e);

                await openModal({
                    tone: "error",
                    title: t("public.onboard.form.feedback.vat_invalid.title"),
                    content: <p className="tp-xs">{t("public.onboard.form.feedback.vat_invalid.message")}</p>,
                    primaryAction: {
                        label: t("public.onboard.form.feedback.vat_invalid.label")
                    }
                });

                return;
            }
        }

        // --- Finalizzazione SSO ---
        try {

            /**
             * Risolve il valore di un campo non editabile (hidden).
             */
            const resolveTokenValue = (hurlId: string): string => {
                if (hurlId === "profilePictureRef") {
                    return (formData["business_email"] ?? "").trim();
                }
                const fromClaim = TOKEN_FIELDS[hurlId]?.fromClaims?.(idTokenClaims, derivedCountry);
                return fromClaim ?? "";
            };

            const allFields = formConfig?.fields ?? [];

            /**
             * Costruisce la lista di campi per il payload. Vengono scartati con
             * `flatMap` solo i campi `hidden && readOnly`; tutti gli altri sono inviati.
             * Per ogni campo il valore è risolto in base alla visibilità:
             * - Campi visibili → valore inserito dall'utente nel form
             * - Campi hidden e non readOnly → valore risolto dal token/config tramite resolveTokenValue
             * L'hurlId e il type vengono passati così come arrivano dalla config CMS,
             * senza mapping aggiuntivo.
             */
            const fieldsPayload: FormFieldPayload[] = allFields.flatMap(field => {
                const { hurlId, type } = field;

                if (field.cmsConfig.hidden && field.readOnly) {
                    return [];
                }

                const value = field.cmsConfig.hidden && !field.readOnly
                    ? resolveTokenValue(hurlId)
                    : (formData[hurlId] ?? "").trim();

                return [{
                    hurlId,
                    type,
                    values: [value]
                }];
            });

            await UserService.finalizeSso({
                externalId: idTokenClaims.sub,
                fields: fieldsPayload,
            });

            const token = await getValidAccessToken(false);
            const result = await runBootstrap({token, b2cCountry: derivedCountry});

            if (!result.ok) {
                throw new Error("Onboarding failed");
            }

            navigate(buildRoute(AppRoute.HOME), {replace: true});

        } catch (e) {
            console.log("Error onboarding", e);

            // Partita IVA già utilizzata: l'API risponde 400 con messageCode "USER-400-0001"
            if (isApiException(e) && e.messageCode === "USER-400-0001") {

                // Messaggio gestibile da CMS (validationMessage.errorModalMessage del campo piva),
                // con fallback sulla chiave i18n.
                const pivaField = formConfig?.fields?.find(f => f.hurlId === "piva");

                await openModal({
                    tone: "error",
                    title: t("public.onboard.form.feedback.generic_error.title"),
                    content: <p className="tp-xs">{errorModalMessage(pivaField, t, "public.onboard.form.feedback.vat_already_used.message")}</p>,
                    primaryAction: {
                        label: t("public.onboard.form.feedback.generic_error.label")
                    }
                });

                return;
            }

            await openModal({
                tone: "error",
                title: t("public.onboard.form.feedback.generic_error.title"),
                content: <p className="tp-xs">{t("public.onboard.form.feedback.generic_error.message")}</p>,
                primaryAction: {
                    label: t("public.onboard.form.feedback.generic_error.label")
                }
            });
        }
    };

    if (isFormLoading) {
        return <OnboardFormSkeleton/>;
    }

    if (isFormError) {
        return (
            <div className="onboardForm__state tp-xs">
                {t("public.onboard.form.feedback.generic_error.message")}
            </div>
        );
    }

    return (
        <form className="form onboardForm" onSubmit={handleSubmit(submitFn)}>
            <div className="onboardForm__fields">
                {visibleFields.map(field => {
                    const hurlId = field.hurlId;
                    const error = errors[hurlId]?.message as string | undefined;

                    switch (field.type) {
                        case FORM_FIELD_TYPE.CHECKBOX:
                        case FORM_FIELD_TYPE.PRIVACY:
                            return (
                                <PrivacyField
                                    key={field.logicId}
                                    control={control}
                                    name={hurlId}
                                    disabled={inputsDisabled}
                                    error={error}
                                    withDivider={false}
                                    text={field.cmsConfig.additionalText}
                                    agreeLabel={removeParenthesesContent(field.cmsConfig.componentConfig?.options?.[0]?.label?.title) || undefined}
                                    singleOption={!!field.mandatory}
                                />
                            );

                        default:
                            return (
                                <TextField
                                    key={field.logicId}
                                    control={control}
                                    name={hurlId}
                                    label={removeParenthesesContent(field.cmsConfig.title)}
                                    description={field.cmsConfig.additionalText || undefined}
                                    type={field.type === FORM_FIELD_TYPE.EMAIL ? "email" : "text"}
                                    disabled={inputsDisabled || field.readOnly}
                                    error={error}
                                />
                            );
                    }
                })}
            </div>

            <div className="onboardForm__submit">
                <button type="submit" disabled={formDisabled} className="cta cta--medium">
                    <span className="label">
                        {isSubmitting ? t("public.onboard.form.submitting.label") : t("public.onboard.form.submit.label")}
                    </span>
                    <span className="icon"><ChevronRightIcon/></span>
                </button>
            </div>

        </form>
    );
}